<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4" style="background-color: rgba(15, 23, 42, 0.75); backdrop-filter: blur(3px);">
    <div class="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden border border-gray-100 text-left animate-fadeIn">
      
      <!-- Header -->
      <div style="background-color: #1e293b;" class="px-6 py-4 flex items-center justify-between text-white">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-xl">
            <i class="fa fa-calculator"></i>
          </div>
          <div>
            <h3 class="font-bold text-base md:text-lg leading-tight text-white">Arqueo y Cierre de Caja</h3>
            <p class="text-xs text-gray-300 mt-0.5">
              {{ registerName || 'Caja Registradora' }} &bull; Cajero: {{ cashierName }}
            </p>
          </div>
        </div>
        <button
          type="button"
          @click="close"
          class="text-gray-400 hover:text-white text-2xl font-bold p-1 leading-none transition-colors cursor-pointer"
        >
          &times;
        </button>
      </div>

      <!-- Admin Tab Selector (si es admin) -->
      <div v-if="isAdmin && !reportData" class="flex border-b border-gray-200 bg-gray-50 text-xs font-bold">
        <button
          type="button"
          @click="activeTab = 'audit'"
          class="flex-1 py-3 text-center transition-colors cursor-pointer"
          :style="activeTab === 'audit' ? 'border-bottom: 2px solid #4f46e5; color: #4f46e5; background-color: #ffffff;' : 'color: #6b7280;'"
        >
          <i class="fa fa-calculator mr-1"></i> Realizar Arqueo / Cierre
        </button>
        <button
          type="button"
          @click="activeTab = 'pin'"
          class="flex-1 py-3 text-center transition-colors cursor-pointer"
          :style="activeTab === 'pin' ? 'border-bottom: 2px solid #4f46e5; color: #4f46e5; background-color: #ffffff;' : 'color: #6b7280;'"
        >
          <i class="fa fa-key mr-1"></i> Configurar Clave Admin
        </button>
      </div>

      <!-- VISTA 1: Formulario de Arqueo Ciego -->
      <div v-if="activeTab === 'audit' && !reportData" class="p-6">
        <form @submit.prevent>
          
          <!-- Banner Informativo Arqueo Ciego -->
          <div class="mb-4 p-3 rounded-xl bg-indigo-50 border border-indigo-200 text-xs text-indigo-900 flex items-start space-x-2">
            <i class="fa fa-eye-slash text-indigo-600 mt-0.5 text-sm"></i>
            <div>
              <span class="font-bold block">Arqueo Ciego de Seguridad</span>
              Cuente y coloque el dinero físico exacto presente en la gaveta. No se mostrarán los montos del sistema hasta completar el arqueo.
            </div>
          </div>

          <!-- Clave de Administrador (Solo Requerida para Arqueo Parcial) -->
          <div class="mb-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200">
            <label class="block text-xs font-bold uppercase text-amber-900 mb-1 flex items-center justify-between">
              <span><i class="fa fa-lock mr-1.5 text-amber-600"></i> Clave de Autorización Admin</span>
              <span class="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Solo para Arqueo Parcial</span>
            </label>
            <input
              type="password"
              v-model="form.admin_pin"
              class="w-full px-3.5 py-2 rounded-lg text-sm bg-white font-bold tracking-widest text-gray-900 border border-amber-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Ingrese clave de Admin solo si realizará arqueo parcial"
            />
            <span class="text-[11px] text-amber-800 font-medium mt-1 block">
              Solo se solicita clave para consultar el <strong>arqueo parcial</strong>.
            </span>
          </div>

          <!-- Campos de Conteo Físico -->
          <div class="space-y-3 mb-4">
            <h4 class="text-xs font-black uppercase tracking-wider text-gray-700">
              <i class="fa fa-money-bill-wave text-emerald-600 mr-1"></i> Dinero Físico en Gaveta:
            </h4>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <!-- Dólares -->
              <div class="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <label class="block text-xs font-bold text-gray-700 mb-1">Dólares ($ USD)</label>
                <div class="relative">
                  <span class="absolute left-2.5 top-2 text-xs font-bold text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="form.counted_usd"
                    required
                    class="w-full pl-6 pr-2 py-1.5 rounded-lg text-sm font-black text-gray-900 bg-white border border-gray-300"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <!-- Pesos COP -->
              <div class="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <label class="block text-xs font-bold text-gray-700 mb-1">Pesos (COP)</label>
                <div class="relative">
                  <span class="absolute left-2.5 top-2 text-xs font-bold text-gray-500">COP</span>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    v-model.number="form.counted_cop"
                    required
                    class="w-full pl-11 pr-2 py-1.5 rounded-lg text-sm font-black text-gray-900 bg-white border border-gray-300"
                    placeholder="0"
                  />
                </div>
              </div>

              <!-- Bolívares Bs -->
              <div class="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <label class="block text-xs font-bold text-gray-700 mb-1">Bolívares (Bs)</label>
                <div class="relative">
                  <span class="absolute left-2.5 top-2 text-xs font-bold text-gray-500">Bs.</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="form.counted_bs"
                    required
                    class="w-full pl-8 pr-2 py-1.5 rounded-lg text-sm font-black text-gray-900 bg-white border border-gray-300"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Observaciones / Notas -->
          <div class="mb-5">
            <label class="block text-xs font-bold uppercase text-gray-600 mb-1">Observaciones (Opcional)</label>
            <input
              type="text"
              v-model="form.notes"
              class="w-full px-3 py-2 rounded-lg text-xs text-gray-800 border border-gray-300"
              placeholder="Ej: Cuadre de turno tarde, billetes deteriorados..."
            />
          </div>

          <!-- Botones de Acción -->
          <div class="flex flex-col sm:flex-row items-center justify-between gap-2 pt-3 border-t border-gray-100">
            <button
              type="button"
              @click="close"
              class="w-full sm:w-auto px-4 py-2.5 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancelar
            </button>

            <div class="flex items-center space-x-2 w-full sm:w-auto justify-end">
              <!-- Arqueo Parcial -->
              <button
                type="button"
                @click="processAudit('audit')"
                :disabled="loading"
                class="flex-1 sm:flex-none px-4 py-2.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-300 rounded-lg transition-all cursor-pointer"
                :class="loading ? 'opacity-50 cursor-not-allowed' : ''"
                title="Muestra el reporte de cuadre sin cerrar la caja (Requiere clave de Admin)"
              >
                <i class="fa fa-clipboard-check mr-1"></i> Arqueo Parcial
              </button>

              <!-- Cerrar Caja -->
              <button
                type="button"
                @click="processAudit('close')"
                :disabled="loading"
                class="flex-1 sm:flex-none px-5 py-2.5 text-xs font-extrabold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-md transition-all flex items-center justify-center space-x-1 cursor-pointer"
                :class="loading ? 'opacity-50 cursor-not-allowed' : ''"
              >
                <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fa fa-door-closed mr-1"></i>
                <span>Finalizar y Cerrar Caja</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <!-- VISTA 2: Reporte de Cuadre Resultante -->
      <div v-else-if="reportData" class="p-6">
        <div id="thermal-ticket" class="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-xs">
          <!-- Cabecera del Reporte -->
          <div class="text-center pb-3 border-b border-dashed border-gray-300">
            <h4 class="font-black text-sm uppercase text-gray-900">Reporte de Arqueo de Caja</h4>
            <p class="font-bold text-gray-700">{{ reportData.cash_register_name }}</p>
            <p class="text-[11px] text-gray-500">Cajero: {{ reportData.cashier_name }}</p>
            <p class="text-[10px] text-gray-400">Fecha: {{ reportData.closed_at }}</p>
            <div class="mt-1">
              <span
                class="inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase"
                :class="isClosedReport ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'"
              >
                {{ isClosedReport ? 'Cierre de Caja Definitivo' : 'Arqueo Parcial (Caja Sigue Abierta)' }}
              </span>
            </div>
          </div>

          <!-- Tabla de Resultados por Moneda -->
          <div class="pt-3 space-y-3">
            <!-- Dólares -->
            <div class="p-2.5 rounded-lg border bg-white shadow-xs">
              <div class="flex justify-between items-center font-black text-sm text-gray-800 border-b pb-1 mb-1">
                <span>Dólares ($ USD)</span>
                <span
                  class="text-[11px] px-2 py-0.5 rounded font-black uppercase"
                  :class="getDiffBadgeClass(reportData.differences.usd.status)"
                >
                  {{ formatDiffText(reportData.differences.usd) }}
                </span>
              </div>
              <div class="grid grid-cols-3 text-[11px] text-gray-600 pt-1">
                <div>Inicial: <strong class="text-gray-900">${{ formatNum(reportData.opening.usd) }}</strong></div>
                <div>Esperado: <strong class="text-gray-900">${{ formatNum(reportData.expected.usd) }}</strong></div>
                <div>Contado: <strong class="text-gray-900">${{ formatNum(reportData.counted.usd) }}</strong></div>
              </div>
            </div>

            <!-- Pesos COP -->
            <div class="p-2.5 rounded-lg border bg-white shadow-xs">
              <div class="flex justify-between items-center font-black text-sm text-gray-800 border-b pb-1 mb-1">
                <span>Pesos (COP)</span>
                <span
                  class="text-[11px] px-2 py-0.5 rounded font-black uppercase"
                  :class="getDiffBadgeClass(reportData.differences.cop.status)"
                >
                  {{ formatDiffText(reportData.differences.cop) }}
                </span>
              </div>
              <div class="grid grid-cols-3 text-[11px] text-gray-600 pt-1">
                <div>Inicial: <strong class="text-gray-900">{{ formatNum(reportData.opening.cop, 0) }}</strong></div>
                <div>Esperado: <strong class="text-gray-900">{{ formatNum(reportData.expected.cop, 0) }}</strong></div>
                <div>Contado: <strong class="text-gray-900">{{ formatNum(reportData.counted.cop, 0) }}</strong></div>
              </div>
            </div>

            <!-- Bolívares Bs -->
            <div class="p-2.5 rounded-lg border bg-white shadow-xs">
              <div class="flex justify-between items-center font-black text-sm text-gray-800 border-b pb-1 mb-1">
                <span>Bolívares (Bs)</span>
                <span
                  class="text-[11px] px-2 py-0.5 rounded font-black uppercase"
                  :class="getDiffBadgeClass(reportData.differences.bs.status)"
                >
                  {{ formatDiffText(reportData.differences.bs) }}
                </span>
              </div>
              <div class="grid grid-cols-3 text-[11px] text-gray-600 pt-1">
                <div>Inicial: <strong class="text-gray-900">{{ formatNum(reportData.opening.bs) }}</strong></div>
                <div>Esperado: <strong class="text-gray-900">{{ formatNum(reportData.expected.bs) }}</strong></div>
                <div>Contado: <strong class="text-gray-900">{{ formatNum(reportData.counted.bs) }}</strong></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botones de Reporte -->
        <div class="flex items-center justify-between pt-2">
          <button
            type="button"
            @click="printTicket"
            class="px-4 py-2 text-xs font-bold text-gray-800 bg-white border border-gray-300 hover:bg-gray-100 rounded-lg shadow-sm flex items-center space-x-1.5 cursor-pointer"
          >
            <i class="fa fa-print"></i>
            <span>Imprimir Comprobante</span>
          </button>

          <button
            type="button"
            @click="finishReport"
            class="px-6 py-2 text-xs font-extrabold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-md cursor-pointer"
          >
            <span>Aceptar y Continuar</span>
          </button>
        </div>
      </div>

      <!-- VISTA 3: Configurar Clave de Administrador (Solo Admin) -->
      <div v-else-if="activeTab === 'pin'" class="p-6">
        <form @submit.prevent="submitPinChange">
          <div class="p-3 mb-4 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900">
            Esta clave o PIN es la que solicitará el sistema a los cajeros para autorizar cualquier <strong>retiro de efectivo</strong> o <strong>arqueo/cierre de caja</strong>.
          </div>

          <div class="mb-4">
            <label class="block text-xs font-bold uppercase text-gray-700 mb-1.5">Nueva Clave de Autorización</label>
            <input
              type="password"
              v-model="pinForm.withdrawal_pin"
              required
              minlength="4"
              maxlength="20"
              class="w-full px-3 py-2.5 rounded-lg text-sm font-bold tracking-wider text-gray-900 border border-gray-300"
              placeholder="Ej. 1234 o clave segura"
            />
          </div>

          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              type="button"
              @click="activeTab = 'audit'"
              class="px-4 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              Volver al Arqueo
            </button>
            <button
              type="submit"
              :disabled="loadingPin"
              class="px-5 py-2 text-xs md:text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow cursor-pointer"
            >
              <i v-if="loadingPin" class="fa fa-spinner fa-spin mr-1"></i>
              <span>Guardar Nueva Clave</span>
            </button>
          </div>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CashAuditModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    cashRegisterId: {
      type: [Number, String],
      default: null
    },
    registerName: {
      type: String,
      default: ''
    },
    cashierName: {
      type: String,
      default: ''
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isLoggingOut: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      activeTab: 'audit',
      loading: false,
      loadingPin: false,
      reportData: null,
      isClosedReport: false,
      form: {
        admin_pin: '',
        counted_usd: '',
        counted_cop: '',
        counted_bs: '',
        notes: ''
      },
      pinForm: this.$inertia.form({
        withdrawal_pin: ''
      })
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.resetState()
      }
    }
  },

  methods: {
    close() {
      this.resetState()
      this.$emit('close')
    },

    resetState() {
      this.reportData = null
      this.isClosedReport = false
      this.activeTab = 'audit'
      this.form = {
        admin_pin: '',
        counted_usd: '',
        counted_cop: '',
        counted_bs: '',
        notes: ''
      }
    },

    async processAudit(action) {
      if (action === 'audit' && !this.form.admin_pin) {
        alert('Para realizar un arqueo parcial debe ingresar la clave de administrador.')
        return
      }

      if (action === 'close' && !confirm('¿Está seguro de que desea finalizar el arqueo y CERRAR la caja? No se podrán registrar más ventas en este turno.')) {
        return
      }

      this.loading = true

      try {
        const response = await axios.post('/cash-registers/audit-or-close', {
          cash_register_id: this.cashRegisterId,
          admin_pin: this.form.admin_pin,
          counted_usd: parseFloat(this.form.counted_usd) || 0,
          counted_cop: parseFloat(this.form.counted_cop) || 0,
          counted_bs: parseFloat(this.form.counted_bs) || 0,
          action: action,
          notes: this.form.notes
        })

        this.loading = false
        const res = response.data

        if (!res.success) {
          alert(res.message || 'Error al procesar el arqueo')
          return
        }

        if (res.is_closed && (!this.isAdmin || !res.report)) {
          alert('✓ ' + (res.message || 'Caja cerrada exitosamente. Los fondos contados han sido registrados para la revisión del administrador.'))
          if (this.isLoggingOut) {
            this.$inertia.delete('/logout')
          } else {
            this.close()
            this.$emit('closed')
          }
          return
        }

        this.reportData = res.report
        this.isClosedReport = res.is_closed

      } catch (err) {
        this.loading = false
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Ocurrió un error al procesar el arqueo.'
        alert(msg)
      }
    },

    finishReport() {
      if (this.isClosedReport) {
        if (this.isLoggingOut) {
          this.$inertia.delete('/logout')
          return
        }
        this.close()
        this.$emit('closed')
      } else {
        this.close()
      }
    },

    getDiffBadgeClass(status) {
      if (status === 'exacto') return 'bg-emerald-100 text-emerald-800'
      if (status === 'sobrante') return 'bg-blue-100 text-blue-800'
      return 'bg-red-100 text-red-800'
    },

    formatDiffText(diff) {
      if (diff.status === 'exacto') return 'Cuadrado (0.00)'
      const sign = diff.amount > 0 ? '+' : ''
      return `${diff.status === 'sobrante' ? 'Sobrante' : 'Faltante'}: ${sign}${this.formatNum(diff.amount)}`
    },

    formatNum(val, decimals = 2) {
      const num = parseFloat(val) || 0
      return num.toLocaleString('es-VE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    },

    printTicket() {
      window.print()
    },

    submitPinChange() {
      if (this.loadingPin) return
      this.loadingPin = true

      this.pinForm.post('/organizations/withdrawal-pin', {
        preserveScroll: true,
        onSuccess: () => {
          this.loadingPin = false
          alert('Clave de administrador actualizada correctamente.')
          this.activeTab = 'audit'
        },
        onError: () => {
          this.loadingPin = false
          alert('Error al guardar la clave.')
        }
      })
    }
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fadeIn {
  animation: fadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@media print {
  body * {
    visibility: hidden;
  }
  #thermal-ticket, #thermal-ticket * {
    visibility: visible;
  }
  #thermal-ticket {
    position: absolute;
    left: 0;
    top: 0;
    width: 80mm;
    margin: 0;
    padding: 10px;
    background: white;
    border: none;
  }
}
</style>
